import { mapActions, mapGetters } from 'vuex';

const SEGMENTATION_BET = 'BET';
const APP_MASTERTAGGO = 'MASTERTAGGO';
const APP_MASTERTAGGO_V3 = 'MASTERTAGGO_V3';
const MAX_API_PER_PAGE = 100;

function mastertagServiceApps(segmentation) {
  if (segmentation === SEGMENTATION_BET) {
    return [APP_MASTERTAGGO];
  }
  if (segmentation) {
    return [APP_MASTERTAGGO_V3];
  }
  // Sem segmentação: une BetConnect (MASTERTAGGO) + demais (MASTERTAGGO_V3).
  return [APP_MASTERTAGGO, APP_MASTERTAGGO_V3];
}

function parsePageArgs(page, perPage) {
  let pageNum = 1;
  let perPageNum = Number(localStorage.getItem('rowsPerPage')) || 10;

  if (typeof page === 'string' && page.includes('page=')) {
    const match = page.match(/page=(\d+)/);
    if (match) pageNum = Number(match[1]) || 1;
  } else if (page) {
    pageNum = Number(page) || 1;
  }

  if (typeof perPage === 'string' && perPage.includes('perPage=')) {
    const match = perPage.match(/perPage=(\d+)/);
    if (match) perPageNum = Number(match[1]) || perPageNum;
  } else if (perPage) {
    perPageNum = Number(perPage) || perPageNum;
  }

  return { pageNum, perPageNum };
}

function isSystemOrigin(origin) {
  return String(origin || '')
    .trim()
    .toLowerCase()
    .startsWith('sistema:');
}

function mastertagLogUserName(item, resolvedName) {
  if (item && item.user_id) {
    return resolvedName || '-';
  }
  if (isSystemOrigin(item && item.origin)) {
    return 'Sistema';
  }
  return 'Não identificado';
}

function mastertagLogUserEmail(item, resolvedEmail) {
  if (isSystemOrigin(item && item.origin) || !(item && item.user_id)) {
    return '-';
  }
  return resolvedEmail || '-';
}

function sortLogsByCreatedAtDesc(logs) {
  return [...logs].sort((a, b) => {
    const aTime = a && a.created_at ? new Date(a.created_at).getTime() : 0;
    const bTime = b && b.created_at ? new Date(b.created_at).getTime() : 0;
    return bTime - aTime;
  });
}

function buildPaginationMeta(page, perPage, total) {
  const lastPage = Math.max(1, Math.ceil(total / perPage) || 1);
  const safePage = Math.min(Math.max(page, 1), lastPage);
  const from = total === 0 ? 0 : (safePage - 1) * perPage + 1;
  const to = total === 0 ? 0 : Math.min(safePage * perPage, total);

  return {
    data: [],
    current_page: safePage,
    last_page: lastPage,
    per_page: perPage,
    total,
    from,
    to
  };
}

export default {
  methods: {
    ...mapActions('MastertagLog', [
      'ActionSetMastertagLogs',
      'ActionSetFilterMastertagLog',
      'ActionSetMastertagLogSegmentation'
    ]),

    buildMastertagLogFilterQuery() {
      return this.filterMastertagLog || '';
    },

    withSource(logs, serviceApp) {
      return (logs || []).map((item) => ({
        ...item,
        _source: serviceApp,
        row_key: `${serviceApp}-${item.id}`
      }));
    },

    async fetchMastertagLogPage(serviceApp, filterQuery, page, perPage) {
      let query = filterQuery || '';
      query += (query ? '%26' : '') + `page=${page}%26perPage=${perPage}`;

      const { data, status } = await this.$http.get(
        `/redirect?app=${serviceApp}&path=/api/v1/partner/configs/change-logs?` + query
      );

      if (status !== 200 || !data) {
        return { rows: [], total: 0 };
      }

      return {
        rows: this.withSource(data.data || [], serviceApp),
        total: Number(data.total) || 0,
        last_page: Number(data.last_page) || 1
      };
    },

    async fetchMastertagLogsFromApp(serviceApp, filterQuery, maxRows) {
      const rows = [];
      let total = 0;
      let page = 1;
      let lastPage = 1;

      do {
        const result = await this.fetchMastertagLogPage(
          serviceApp,
          filterQuery,
          page,
          MAX_API_PER_PAGE
        );
        total = result.total;
        lastPage = result.last_page || 1;
        rows.push(...(result.rows || []));

        if (!result.rows || !result.rows.length) break;
        page += 1;
      } while (rows.length < maxRows && page <= lastPage);

      return {
        rows: rows.slice(0, maxRows),
        total
      };
    },

    async enrichMastertagLogs(logs) {
      const userIds = [...new Set((logs || []).map((item) => item.user_id).filter(Boolean))];
      if (!userIds.length) {
        return (logs || []).map((item) => ({
          ...item,
          user_name: mastertagLogUserName(item, item.user_name),
          user_email: mastertagLogUserEmail(item, item.user_email)
        }));
      }

      try {
        const { data, status } = await this.$http.get(`/user/get-group-by-id/${userIds.join(',')}`);
        if (status !== 200 || !Array.isArray(data)) {
          return logs;
        }

        const userMap = data.reduce((acc, user) => {
          acc[user.id] = user;
          return acc;
        }, {});

        return logs.map((item) => ({
          ...item,
          user_name: mastertagLogUserName(
            item,
            userMap[item.user_id] && userMap[item.user_id].name
          ),
          user_email: mastertagLogUserEmail(
            item,
            userMap[item.user_id] && userMap[item.user_id].email
          )
        }));
      } catch (error) {
        return (logs || []).map((item) => ({
          ...item,
          user_name: mastertagLogUserName(item, item.user_name),
          user_email: mastertagLogUserEmail(item, item.user_email)
        }));
      }
    },

    async getMastertagLogs(page, perPage) {
      const { pageNum, perPageNum } = parsePageArgs(page, perPage);
      const filterQuery = this.buildMastertagLogFilterQuery();
      const apps = mastertagServiceApps(this.mastertagLogSegmentation);
      const need = pageNum * perPageNum;

      this.onLoading(true);
      try {
        const results = await Promise.all(
          apps.map((app) => this.fetchMastertagLogsFromApp(app, filterQuery, need))
        );

        const merged = sortLogsByCreatedAtDesc(results.flatMap((result) => result.rows || []));
        const total = results.reduce((sum, result) => sum + (result.total || 0), 0);
        const pagination = buildPaginationMeta(pageNum, perPageNum, total);
        const sliceStart = (pagination.current_page - 1) * perPageNum;
        const pageRows = merged.slice(sliceStart, sliceStart + perPageNum);
        const enriched = await this.enrichMastertagLogs(pageRows);

        this.ActionSetMastertagLogs(enriched);
        this.ActionSetPagination({
          ...pagination,
          data: enriched
        });
      } catch (error) {
        if (error.response && error.response.status === 500)
          this.errorNotify(this.$t('problem_try_again_later'));
        else this.errorNotify(error);
      } finally {
        this.onLoading(false);
      }
    },

    async getAllMastertagLogs() {
      const filterQuery = this.buildMastertagLogFilterQuery();
      const apps = mastertagServiceApps(this.mastertagLogSegmentation);
      const allRows = [];

      this.onLoading(true);
      try {
        for (const app of apps) {
          let page = 1;
          let lastPage = 1;

          do {
            const result = await this.fetchMastertagLogPage(app, filterQuery, page, MAX_API_PER_PAGE);
            allRows.push(...(result.rows || []));
            lastPage = result.last_page || 1;
            page += 1;
          } while (page <= lastPage);
        }

        const sorted = sortLogsByCreatedAtDesc(allRows);
        return this.enrichMastertagLogs(sorted);
      } catch (error) {
        if (error.response && error.response.status === 500)
          this.errorNotify(this.$t('problem_try_again_later'));
        else this.errorNotify(error);
        return [];
      } finally {
        this.onLoading(false);
      }
    }
  },

  computed: {
    ...mapGetters('MastertagLog', [
      'mastertagLogs',
      'filterMastertagLog',
      'mastertagLogSegmentation'
    ]),

    mastertagLogColumns() {
      return [
        {
          name: 'user_id',
          label: this.$t('user_id'),
          field: 'user_id',
          align: 'left',
          sortable: true
        },
        {
          name: 'user_name',
          label: this.$t('user_name'),
          field: 'user_name',
          align: 'left',
          sortable: true
        },
        {
          name: 'user_email',
          label: this.$t('email'),
          field: 'user_email',
          align: 'left',
          sortable: true
        },
        {
          name: 'progid',
          label: this.$t('campaign_id'),
          field: 'progid',
          align: 'left',
          sortable: true
        },
        {
          name: 'siteid',
          label: this.$t('channel_id'),
          field: 'siteid',
          align: 'left',
          sortable: true
        },
        {
          name: 'tag_type',
          label: this.$t('type'),
          field: 'tag_type',
          align: 'left'
        },
        {
          name: 'functionality',
          label: this.$t('functionality'),
          field: 'functionality',
          align: 'left'
        },
        {
          name: 'operation',
          label: this.$t('operation'),
          field: 'operation',
          align: 'left'
        },
        {
          name: 'origin',
          label: this.$t('origin'),
          field: 'origin',
          align: 'left'
        },
        {
          name: 'ip_address',
          label: this.$t('ip_address'),
          field: 'ip_address',
          align: 'left'
        },
        {
          name: 'geolocation',
          label: this.$t('geolocation'),
          field: 'geolocation',
          align: 'left'
        },
        {
          name: 'user_agent',
          label: this.$t('user_agent'),
          field: 'user_agent',
          align: 'left'
        },
        {
          name: 'request_id',
          label: this.$t('request_id'),
          field: 'request_id',
          align: 'left'
        },
        {
          name: 'old_value',
          label: this.$t('previous_value'),
          field: 'old_value',
          align: 'left'
        },
        {
          name: 'new_value',
          label: this.$t('current_value'),
          field: 'new_value',
          align: 'left'
        },
        {
          name: 'created_at',
          label: this.$t('change_date'),
          field: 'created_at',
          align: 'left',
          sortable: true,
          format: (val) => (val ? this.$moment(val).format('L LTS') : '-')
        }
      ];
    }
  }
};
